import { fixedUi, fixedUiTpl } from '@/i18n/fixedUi';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import RequiredMark from '@/tiktok-platform/components/RequiredMark';
import CategoryCascaderSelect from '@/tiktok-platform/components/CategoryCascaderSelect';
import TiktokSwitch from '@/tiktok-platform/components/TiktokSwitch';
import TiktokStructuredSizeChartPanel, {
  MEASUREMENT_TYPE_LABELS,
  UNIT_SYSTEM_OPTIONS,
  buildSizeChartDataFromDraft,
  defaultSelectedOptionIdsForType,
  hydrateStructuredDraft,
  optionsForType,
  validateStructuredSizeChartDraft,
} from '@/tiktok-platform/modules/templates/TiktokStructuredSizeChartPanel';
import { flattenLeafCategories } from '@/tiktok-platform/utils/tiktokCategoryTree';
import { nextTemplatePriority } from '@/tiktok-platform/utils/tiktokTemplatePriority';
import {
  conflictToastMessage,
  isTemplateVersionConflictError,
  parseTemplateVersion,
} from '@/tiktok-platform/utils/tiktokTemplateVersionGuard';
import TemplateVersionConflictMark from '@/tiktok-platform/modules/products/components/TemplateVersionConflictMark';
import { useTiktokShopOptional } from '@/tiktok-platform/context/TiktokShopContext';
import { loadShopCategoriesCached } from '@/tiktok-platform/utils/tiktokTemplateScopeCache';
import { tiktokPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';

function buildOptionMetaById(groups, measurementType) {
  const map = {};
  optionsForType(groups, measurementType).forEach((o) => {
    if (o?.id) map[o.id] = o;
  });
  return map;
}

function fetchSizeChartDetail(officialMode, shopId, templateId, templateScope) {
  if (officialMode) {
    return adminAPI.getOfficialTiktokSizeChartTemplate(templateId);
  }
  return tiktokPlatformAPI.getSizeChartTemplate(shopId, templateId, { scope: templateScope });
}

function fetchSizeChartList(officialMode, shopId, params) {
  if (officialMode) {
    return adminAPI.listOfficialTiktokSizeChartTemplates(params);
  }
  return tiktokPlatformAPI.listSizeChartTemplates(shopId, params);
}

function saveSizeChartTemplate(officialMode, shopId, templateId, body) {
  if (officialMode) {
    return templateId
      ? adminAPI.updateOfficialTiktokSizeChartTemplate(templateId, body)
      : adminAPI.createOfficialTiktokSizeChartTemplate(body);
  }
  return templateId
    ? tiktokPlatformAPI.updateSizeChartTemplate(shopId, templateId, body)
    : tiktokPlatformAPI.createSizeChartTemplate(shopId, body);
}

/**
 * 新建/编辑尺码库：
 * - 官方库：只落本地官方表，借参考店拉 measurement 规则，不创建平台尺码表
 * - 店铺库：可创建到平台；由官方复制来的本地行可在列表「提交后台」
 */
export default function SizeChartTemplateCreateModal({
  open,
  shopId: shopIdProp = '',
  templateId = null,
  templateScope = 'PERSONAL',
  officialMode = false,
  shopType = '',
  referenceShopId = '',
  onClose,
  onSaved,
}) {
  useTranslation('workbench');
  const isEdit = Boolean(templateId);
  const tiktokShopCtx = useTiktokShopOptional();
  const [catalogReferenceShopId, setCatalogReferenceShopId] = useState('');
  const shopId = String(
    shopIdProp || referenceShopId || catalogReferenceShopId || tiktokShopCtx?.activeShop?.id || '',
  ).trim();
  /** 拉类目 rules / 销售属性用的店铺：官方模式用 catalog 借店 */
  const rulesShopId = shopId;

  const [name, setName] = useState('');
  const [styleNo, setStyleNo] = useState('');
  const [remark, setRemark] = useState('');
  const [autoRuleEnabled, setAutoRuleEnabled] = useState(false);
  const [sortOrder, setSortOrder] = useState(0);
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState('');
  const [productTypeId, setProductTypeId] = useState('');
  const [categoryPathLabel, setCategoryPathLabel] = useState('');
  const [detailLoading, setDetailLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const savingLockRef = useRef(false);
  const [metaLoading, setMetaLoading] = useState(false);
  const [templateVersion, setTemplateVersion] = useState(null);
  const [versionConflict, setVersionConflict] = useState(false);
  const [reloadingVersion, setReloadingVersion] = useState(false);
  const [platformBusinessId, setPlatformBusinessId] = useState('');
  const [sizeChartSupported, setSizeChartSupported] = useState(true);
  const [sizeChartGroups, setSizeChartGroups] = useState([]);
  const [structDraft, setStructDraft] = useState(() => hydrateStructuredDraft({}));
  const [structSeed, setStructSeed] = useState(null);
  /** 保存前弹窗：测量类型 / 单位制（店铺写平台时用） */
  const [saveMetaOpen, setSaveMetaOpen] = useState(false);
  const [saveMeasurementType, setSaveMeasurementType] = useState('BODY_MEASUREMENTS');
  const [saveUnitSystem, setSaveUnitSystem] = useState('METRIC');

  const categoryOptions = useMemo(
    () => flattenLeafCategories(categories).map((opt) => ({
      value: opt.categoryId,
      label: opt.label,
      productTypeId: opt.productTypeId,
    })),
    [categories],
  );

  useEffect(() => {
    if (!open) return;
    setName('');
    setStyleNo('');
    setRemark('');
    setAutoRuleEnabled(false);
    setSortOrder(0);
    setCategoryId('');
    setProductTypeId('');
    setCategoryPathLabel('');
    setDetailLoading(false);
    setSaving(false);
    setTemplateVersion(null);
    setVersionConflict(false);
    setReloadingVersion(false);
    setPlatformBusinessId('');
    setStructDraft(hydrateStructuredDraft({}));
    setStructSeed(null);
    setSizeChartSupported(true);
    setSizeChartGroups([]);
    setSaveMetaOpen(false);
    setSaveMeasurementType('BODY_MEASUREMENTS');
    setSaveUnitSystem('METRIC');
    setCatalogReferenceShopId('');
  }, [open, templateId]);

  useEffect(() => {
    if (!open || (!officialMode && !shopIdProp && !referenceShopId && !tiktokShopCtx?.activeShop?.id)) {
      if (!officialMode) setCategories([]);
      return undefined;
    }
    if (officialMode && !shopType) {
      setCategories([]);
      setCatalogReferenceShopId('');
      return undefined;
    }
    let cancelled = false;
    setMetaLoading(true);
    const load = officialMode
      ? adminAPI.getOfficialTiktokCatalogCategories({ shopType })
      : loadShopCategoriesCached(
        String(shopIdProp || referenceShopId || tiktokShopCtx?.activeShop?.id || '').trim(),
        () => tiktokPlatformAPI.getShopCategories(
          String(shopIdProp || referenceShopId || tiktokShopCtx?.activeShop?.id || '').trim(),
        ),
      ).then((categories) => ({ categories }));
    load
      .then((res) => {
        if (cancelled) return;
        if (officialMode) {
          setCatalogReferenceShopId(String(res?.shopId || '').trim());
        }
        setCategories(res?.categories ?? res?.items ?? res?.list ?? res ?? []);
      })
      .catch(() => {
        if (!cancelled) {
          setCategories([]);
          if (officialMode) setCatalogReferenceShopId('');
        }
      })
      .finally(() => {
        if (!cancelled) setMetaLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopIdProp, referenceShopId, officialMode, shopType, tiktokShopCtx?.activeShop?.id]);

  useEffect(() => {
    if (!open || !templateId || (!officialMode && !shopId)) return undefined;
    let cancelled = false;
    setDetailLoading(true);
    fetchSizeChartDetail(officialMode, shopId, templateId, templateScope)
      .then((res) => {
        if (cancelled) return;
        const item = res?.item || res || {};
        const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
        setName(String(item.name || '').trim());
        setStyleNo(String(item.styleNo || '').trim());
        setRemark(String(item.remark || '').trim());
        setAutoRuleEnabled(item.autoRuleEnabled === true || item.autoRuleEnabled === 1);
        {
          const order = Number(item.sortOrder);
          setSortOrder(Number.isFinite(order) ? order : 0);
        }
        setCategoryId(String(item.categoryId || payload.categoryId || ''));
        setProductTypeId(String(item.productTypeId || payload.productTypeId || ''));
        setCategoryPathLabel(String(item.categoryPathLabel || payload.categoryPathLabel || ''));
        setTemplateVersion(parseTemplateVersion(item.version));
        setVersionConflict(false);
        setPlatformBusinessId(String(
          item.platformBusinessId
          || payload.platformBusinessIds?.[0]
          || '',
        ).trim());
        const seed = hydrateStructuredDraft(payload);
        setStructDraft(seed);
        setStructSeed(seed);
      })
      .catch((e) => {
        if (cancelled) return;
        showToast(e?.message || chartText('photoLib.loadTemplateFailed', fixedUi("加载模板失败")), 'error');
        onClose?.();
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, templateId, templateScope, officialMode, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onClose]);

  const measurementTypeChoices = useMemo(() => {
    const seen = new Set();
    const list = (sizeChartGroups || []).map((g) => {
      const id = g.measurement_type || g.measurementType;
      if (!id || seen.has(id)) return null;
      seen.add(id);
      return { id, label: MEASUREMENT_TYPE_LABELS[id] || id };
    }).filter(Boolean);
    if (!list.length) {
      return Object.entries(MEASUREMENT_TYPE_LABELS).map(([id, label]) => ({ id, label }));
    }
    return list;
  }, [sizeChartGroups]);

  if (!open) return null;

  const onCategoryChange = (id, meta) => {
    const hit = categoryOptions.find((opt) => String(opt.value) === String(id));
    setCategoryId(id ? String(id) : '');
    const metaTypeId = meta?.productTypeId != null ? String(meta.productTypeId) : '';
    const hitTypeId = hit?.productTypeId != null ? String(hit.productTypeId) : '';
    const nextTypeId = (metaTypeId && metaTypeId !== '0')
      ? metaTypeId
      : (hitTypeId && hitTypeId !== '0')
        ? hitTypeId
        : (id ? String(id) : '');
    setProductTypeId(nextTypeId);
    setCategoryPathLabel(String(meta?.label || hit?.label || '').trim());
    setStructSeed(null);
    setStructDraft(hydrateStructuredDraft({}));
    setSizeChartSupported(true);
    if (!isEdit && nextTypeId && (officialMode || shopId)) {
      fetchSizeChartList(officialMode, shopId, {
        scope: templateScope,
        productTypeId: nextTypeId,
        page: 1,
        pageSize: 100,
        ...(officialMode ? { shopType } : {}),
      }).then((res) => {
        setSortOrder(nextTemplatePriority(res?.items));
      }).catch(() => {
        setSortOrder(0);
      });
    }
  };

  const reloadTemplateFromServer = async () => {
    if (!templateId || (!officialMode && !shopId)) return;
    setReloadingVersion(true);
    try {
      const res = await fetchSizeChartDetail(officialMode, shopId, templateId, templateScope);
      const item = res?.item || res || {};
      const payload = item.payload && typeof item.payload === 'object' ? item.payload : {};
      setName(String(item.name || '').trim());
      setStyleNo(String(item.styleNo || '').trim());
      setRemark(String(item.remark || '').trim());
      setTemplateVersion(parseTemplateVersion(item.version));
      setPlatformBusinessId(String(item.platformBusinessId || '').trim());
      const seed = hydrateStructuredDraft(payload);
      setStructDraft(seed);
      setStructSeed(seed);
      setVersionConflict(false);
      showToast(chartText('sizeCreate.reloaded', fixedUi('已重新加载尺码库')), 'success');
    } catch (e) {
      showToast(e?.message || chartText('photoLib.reloadFailed', fixedUi("重新加载失败")), 'error');
    } finally {
      setReloadingVersion(false);
    }
  };

  /** 主表单点「保存」：官方直接落本地；店铺先确认测量类型再写平台/本地 */
  const openSaveMeta = () => {
    if (officialMode && !shopType) {
      showToast(chartText('sizeCreate.needShopTypeFirst', fixedUi("请先选择店铺类型")), 'error');
      return;
    }
    if (!officialMode && !shopId) {
      showToast(chartText('returnList.pickShop', fixedUi("请先选择店铺")), 'error');
      return;
    }
    if (!String(styleNo || '').trim()) {
      showToast(chartText('photoLib.needStyleNo', fixedUi("请填写款号")), 'error');
      return;
    }
    if (!String(name || '').trim()) {
      showToast(chartText('photoLib.needName', fixedUi("请填写模板名称")), 'error');
      return;
    }
    if (!productTypeId || !categoryId) {
      showToast(chartText('rrpLib.needCategory', fixedUi("请选择类目")), 'error');
      return;
    }
    if (officialMode && !rulesShopId) {
      showToast(chartText('sizeCreate.noRefValidate', fixedUi('暂无参考店铺，无法按类目规则校验尺码表，请先授权同类型 TikTok 店铺')), 'error');
      return;
    }
    const optionMetaById = buildOptionMetaById(sizeChartGroups, structDraft.measurementType);
    const check = validateStructuredSizeChartDraft(structDraft, {
      required: true,
      requireMeta: false,
      optionMetaById,
    });
    if (!check.ok) {
      showToast(check.message || chartText('templateEditor.fillSizeChart', fixedUi('请完善尺码表')), 'error');
      return;
    }
    // 官方库：不创建平台，直接保存本地官方模板
    if (officialMode) {
      void submit({ forceLocal: true });
      return;
    }
    setSaveMeasurementType(
      structDraft.measurementType
      || measurementTypeChoices[0]?.id
      || 'BODY_MEASUREMENTS',
    );
    setSaveUnitSystem(structDraft.unitSystem || 'METRIC');
    setSaveMetaOpen(true);
  };

  const submit = async ({ forceLocal = false } = {}) => {
    if (savingLockRef.current || saving) return;
    if (officialMode && !shopType) {
      showToast(chartText('sizeCreate.needShopTypeFirst', fixedUi("请先选择店铺类型")), 'error');
      return;
    }
    if (!officialMode && !shopId) {
      showToast(chartText('returnList.pickShop', fixedUi("请先选择店铺")), 'error');
      return;
    }
    const styleNoValue = String(styleNo || '').trim();
    if (!styleNoValue) {
      showToast(chartText('photoLib.needStyleNo', fixedUi("请填写款号")), 'error');
      return;
    }
    const title = String(name || '').trim();
    if (!title) {
      showToast(chartText('photoLib.needName', fixedUi("请填写模板名称")), 'error');
      return;
    }
    if (!productTypeId || !categoryId) {
      showToast(chartText('rrpLib.needCategory', fixedUi("请选择类目")), 'error');
      return;
    }

    const measurementType = String(
      (officialMode || forceLocal ? structDraft.measurementType : saveMeasurementType) || '',
    ).trim() || 'BODY_MEASUREMENTS';
    const unitSystem = String(
      (officialMode || forceLocal ? structDraft.unitSystem : saveUnitSystem) || '',
    ).trim() || 'METRIC';
    const typeOptionIds = new Set(optionsForType(sizeChartGroups, measurementType).map((o) => o.id));
    const keptIds = (structDraft.selectedOptionIds || []).filter((id) => typeOptionIds.has(String(id)));
    const selectedOptionIds = keptIds.length
      ? keptIds
      : defaultSelectedOptionIdsForType(sizeChartGroups, measurementType);
    const draftForSave = {
      ...structDraft,
      measurementType,
      unitSystem,
      selectedOptionIds,
    };
    const optionMetaById = buildOptionMetaById(sizeChartGroups, measurementType);
    const check = validateStructuredSizeChartDraft(draftForSave, {
      required: true,
      requireMeta: !officialMode,
      optionMetaById,
    });
    if (!check.ok) {
      showToast(check.message || chartText('templateEditor.fillSizeChart', fixedUi('请完善尺码表')), 'error');
      return;
    }

    const sizeChartData = buildSizeChartDataFromDraft(draftForSave, optionMetaById);
    // 官方模板永不写平台；店铺侧仍可按类目支持情况创建到平台
    const publishToPlatform = !officialMode && !forceLocal && sizeChartSupported;
    if (!officialMode && !publishToPlatform) {
      showToast(chartText('sizeCreate.unsupportedDraft', fixedUi('该类目不支持尺码表，将仅保存站内草稿')), 'info');
    }

    const localPayload = {
      kind: officialMode ? 'tiktok-official' : 'tiktok-platform',
      source: 'local',
      categoryId,
      productTypeId,
      categoryPathLabel: categoryPathLabel || '',
      sizeChartData,
      officialSizeChart: { mode: 'create', sizeChartData },
      sizeChart: {},
    };

    const body = officialMode
      ? {
        name: title,
        styleNo: styleNoValue,
        remark: String(remark || '').trim() || undefined,
        shopType,
        autoRuleEnabled: Boolean(autoRuleEnabled),
        sortOrder: Number(sortOrder) || 0,
        categoryId,
        productTypeId,
        categoryPathLabel: categoryPathLabel || undefined,
        payload: localPayload,
        ...(isEdit && templateVersion != null ? { version: templateVersion } : {}),
      }
      : {
        name: title,
        styleNo: styleNoValue,
        remark: String(remark || '').trim() || undefined,
        autoRuleEnabled: Boolean(autoRuleEnabled),
        sortOrder: Number(sortOrder) || 0,
        scope: templateScope,
        categoryId,
        productTypeId,
        categoryPathLabel: categoryPathLabel || undefined,
        publishToPlatform,
        ...(publishToPlatform ? { sizeChartData } : { payload: localPayload }),
        ...(isEdit && templateVersion != null ? { version: templateVersion } : {}),
        ...(platformBusinessId ? { platformBusinessId, size_chart_id: platformBusinessId } : {}),
      };

    savingLockRef.current = true;
    setSaving(true);
    try {
      const res = await saveSizeChartTemplate(officialMode, shopId, isEdit ? templateId : null, body);
      if (res?.platformWriteSkipped) {
        const detail = String(res?.platformError || res?.message || '').trim();
        showToast(detail || chartText('sizeCreate.platformWriteLocal', fixedUi('平台写入不可用，已保存到本地')), 'warning');
      } else if (officialMode) {
        showToast(isEdit ? chartText('sizeCreate.officialUpdated', fixedUi('官方尺码模板已更新')) : chartText('sizeCreate.officialSavedLocal', fixedUi('官方尺码模板已保存（仅本地，未创建平台）')), 'success');
      } else {
        showToast(publishToPlatform
          ? (isEdit ? chartText('sizeCreate.updatedPlatform', fixedUi('已更新平台尺码表')) : chartText('sizeCreate.createdPlatform', fixedUi('已创建平台尺码表')))
          : (isEdit ? chartText('sizeCreate.savedLocalTpl', fixedUi('已保存本地尺码模板')) : chartText('sizeCreate.savedLocalTpl', fixedUi('已保存本地尺码模板'))), 'success');
      }
      setSaveMetaOpen(false);
      onSaved?.();
      onClose?.();
    } catch (e) {
      if (isTemplateVersionConflictError(e)) {
        setVersionConflict(true);
        showToast(conflictToastMessage([{ type: 'sizeChart' }]), 'warning');
      } else {
        showToast(e?.message || chartText('modelLib.saveFailed', fixedUi('保存失败')), 'error');
      }
    } finally {
      savingLockRef.current = false;
      setSaving(false);
    }
  };

  const busy = saving || detailLoading || metaLoading;

  return createPortal(
    <div className="tko-batch-modal tko-size-tpl-modal" role="presentation">
      <button
        type="button"
        className="tko-batch-modal__backdrop"
        aria-label={chartText('moduleShell.close', fixedUi("关闭"))}
        onClick={() => { if (!saving) onClose?.(); }}
      />
      <div
        className="tko-batch-modal__panel tko-size-tpl-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tko-size-tpl-create-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tko-batch-modal__head">
          <h2 id="tko-size-tpl-create-title">
            {isEdit
              ? (officialMode ? chartText('sizeCreate.editOfficial', fixedUi('编辑官方尺码模板')) : chartText('sizeCreate.editLib', fixedUi('编辑尺码库')))
              : (officialMode ? chartText('sizeCreate.createOfficial', fixedUi('新建官方尺码模板')) : chartText('sizeCreate.createLib', fixedUi('新建尺码库')))}
          </h2>
          <button
            type="button"
            className="tko-batch-modal__close"
            aria-label={chartText('moduleShell.close', fixedUi("关闭"))}
            disabled={saving}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="tko-batch-modal__body">
          <div className="tko-wizard__form tko-template-create-form">
            <label className="tko-field tko-field--full">
              <span className="tko-field__label">{chartText('productTemplate.colStyleNo', fixedUi("款号"))} <RequiredMark /></span>
              <input
                className="tko-input"
                value={styleNo}
                disabled={busy}
                maxLength={128}
                placeholder={chartText('photoLib.styleNoPh', fixedUi("请输入款号"))}
                onChange={(e) => setStyleNo(e.target.value)}
              />
            </label>

            <label className={`tko-field tko-field--full${versionConflict ? ' tko-field--version-conflict' : ''}`}>
              <span className="tko-field__label">
                {fixedUi("模板名称")} <RequiredMark />
                <TemplateVersionConflictMark
                  active={versionConflict}
                  label={chartText('photoLib.updatedMark', fixedUi('已更新'))}
                  reloading={reloadingVersion}
                  onReload={reloadTemplateFromServer}
                />
              </span>
              <input
                className="tko-input"
                value={name}
                disabled={busy}
                maxLength={128}
                placeholder={chartText('sizeCreate.namePh', fixedUi('如：女装上衣基础尺码表'))}
                onChange={(e) => setName(e.target.value)}
              />
            </label>

            <label className="tko-field tko-field--full">
              <span className="tko-field__label">{chartText('productTemplate.colRemark', fixedUi("备注"))}</span>
              <input
                className="tko-input"
                value={remark}
                disabled={busy}
                maxLength={200}
                placeholder={chartText('photoLib.optional', fixedUi("可选"))}
                onChange={(e) => setRemark(e.target.value)}
              />
            </label>

            <div className="tko-field tko-field--full tko-template-auto-rule">
              <div className="tko-template-auto-rule__switch">
                <TiktokSwitch
                  checked={autoRuleEnabled}
                  disabled={busy}
                  ariaLabel={chartText('productTemplate.colAutoRule', fixedUi('自动规则'))}
                  onChange={setAutoRuleEnabled}
                />
                <span>{chartText('productTemplate.colAutoRule', fixedUi("自动规则"))}</span>
              </div>
              <p className="tko-wizard__inline-hint">
                {chartText('sizeCreate.autoHint', fixedUi('开启后，发品选中该类目时自动带出。优先级仅在同类目内比较，数字越小越优先；同类目不可重复，不同类目互不影响。'))}
              </p>
              <label className="tko-field">
                <span className="tko-field__label">{chartText('productTemplate.colPriority', fixedUi("优先级"))}</span>
                <input
                  className="tko-input"
                  type="number"
                  step={1}
                  value={sortOrder}
                  disabled={busy}
                  onChange={(e) => {
                    const next = Number(e.target.value);
                    setSortOrder(Number.isFinite(next) ? next : 0);
                  }}
                />
              </label>
            </div>

            <label className="tko-field tko-field--full">
              <span className="tko-field__label">{chartText('rrpLib.categoryApply', fixedUi("适用类目"))} <RequiredMark /></span>
              <CategoryCascaderSelect
                shopId={officialMode ? undefined : shopId}
                officialShopType={officialMode ? shopType : undefined}
                categories={categories}
                value={categoryId}
                label={categoryPathLabel}
                disabled={busy || isEdit || metaLoading || (!officialMode && !shopId)}
                placeholder={
                  !officialMode && !shopId
                    ? chartText('returnList.pickShop', fixedUi("请先选择店铺"))
                    : (metaLoading || detailLoading ? chartText('templateEditor.loadingCats', fixedUi('加载类目中…')) : chartText('rrpLib.categorySearch', fixedUi("请选择或搜索类目")))
                }
                emptyText={metaLoading ? chartText('productTemplate.categoriesLoading', fixedUi('类目加载中…')) : chartText('productTemplate.noCategories', fixedUi("暂无可发商品分类"))}
                onChange={onCategoryChange}
              />
              {isEdit && platformBusinessId ? (
                <p className="tko-wizard__inline-hint" style={{ marginTop: 6 }}>
                  {chartText('sizeCreate.platformId', fixedUiTpl('平台尺码表 ID：{0}（类目创建后不可改）', platformBusinessId), { id: platformBusinessId })}
                </p>
              ) : null}
            </label>
          </div>

          {detailLoading ? (
            <p className="tko-wizard__inline-hint">{chartText('photoLib.loadingTemplate', fixedUi("正在加载模板…"))}</p>
          ) : null}

          {officialMode && categoryId && !rulesShopId && !metaLoading ? (
            <p className="tko-wizard__inline-error" role="alert">
              {chartText('sizeCreate.noRefRules', fixedUi('暂无可用参考店铺，无法加载尺码填写规则。请先授权同类型 TikTok 店铺。'))}
            </p>
          ) : null}

          <TiktokStructuredSizeChartPanel
            shopId={rulesShopId}
            categoryId={categoryId}
            initialDraft={structSeed}
            allowWithoutCategory={isEdit}
            disabled={busy || (officialMode && !rulesShopId)}
            showMeasurementMeta={false}
            onDraftChange={setStructDraft}
            onRulesMetaChange={({ supported, groups }) => {
              setSizeChartSupported(supported !== false);
              setSizeChartGroups(Array.isArray(groups) ? groups : []);
            }}
          />
        </div>

        <footer className="tko-batch-modal__foot">
          <button type="button" className="tko-btn tko-btn--secondary" disabled={saving} onClick={onClose}>
            {chartText('shop.cancel', fixedUi('取消'))}
          </button>
          <button
            type="button"
            className="tko-btn tko-btn--primary"
            disabled={busy}
            onClick={openSaveMeta}
          >
            {saving
              ? chartText('tiktokPublish.saving', fixedUi("保存中…"))
              : (officialMode
                ? chartText('sizeCreate.saveOfficial', fixedUi('保存官方模板'))
                : (isEdit
                  ? (sizeChartSupported ? chartText('modelLib.savePlatform', fixedUi('保存到平台')) : chartText('sizeCreate.saveLocal', fixedUi('保存本地')))
                  : (sizeChartSupported ? chartText('sizeCreate.createPlatform', fixedUi('创建到平台')) : chartText('sizeCreate.saveLocal', fixedUi('保存本地')))))}
          </button>
        </footer>
      </div>

      {saveMetaOpen ? (
        <div className="tko-batch-modal tko-size-tpl-save-meta" role="presentation" style={{ zIndex: 1100 }}>
          <button
            type="button"
            className="tko-batch-modal__backdrop"
            aria-label={chartText('moduleShell.close', fixedUi("关闭"))}
            onClick={() => { if (!saving) setSaveMetaOpen(false); }}
          />
          <div
            className="tko-batch-modal__panel tko-attr-tpl-modal__panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="tko-size-tpl-save-meta-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="tko-batch-modal__head">
              <h2 id="tko-size-tpl-save-meta-title">{chartText('sizeCreate.confirmMeta', fixedUi("确认测量参数"))}</h2>
              <button
                type="button"
                className="tko-batch-modal__close"
                aria-label={chartText('moduleShell.close', fixedUi("关闭"))}
                disabled={saving}
                onClick={() => setSaveMetaOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="tko-batch-modal__body">
              <p className="tko-wizard__inline-hint">
                {chartText('sizeCreate.confirmMetaHint', fixedUi('保存前请确认测量类型与单位制（对应平台 Create Size Chart 的 measurement_type / unit_system）。'))}
              </p>
              <div className="tko-wizard__form tko-template-create-form">
                <label className="tko-field tko-field--full">
                  <span className="tko-field__label">{chartText('sizeChartPanel.measureType', fixedUi("测量类型"))} <RequiredMark /></span>
                  <select
                    className="tko-input"
                    value={saveMeasurementType}
                    disabled={saving}
                    onChange={(e) => setSaveMeasurementType(e.target.value)}
                  >
                    {measurementTypeChoices.map((opt) => (
                      <option key={opt.id} value={opt.id}>{({
                        GENERIC_MEASUREMENTS: 'sizeChartPanel.mtGeneric',
                        BODY_MEASUREMENTS: 'sizeChartPanel.mtBody',
                        PRODUCT_MEASUREMENTS: 'sizeChartPanel.mtProduct',
                      }[opt.id]
                        ? chartText({
                          GENERIC_MEASUREMENTS: 'sizeChartPanel.mtGeneric',
                          BODY_MEASUREMENTS: 'sizeChartPanel.mtBody',
                          PRODUCT_MEASUREMENTS: 'sizeChartPanel.mtProduct',
                        }[opt.id], opt.label)
                        : opt.label)}</option>
                    ))}
                  </select>
                </label>
                <label className="tko-field tko-field--full">
                  <span className="tko-field__label">{chartText('sizeChartPanel.unitSystem', fixedUi("单位制"))} <RequiredMark /></span>
                  <select
                    className="tko-input"
                    value={saveUnitSystem}
                    disabled={saving}
                    onChange={(e) => setSaveUnitSystem(e.target.value)}
                  >
                    {UNIT_SYSTEM_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{({
                        METRIC: 'sizeChartPanel.unitMetric',
                        IMPERIAL: 'sizeChartPanel.unitImperial',
                      }[opt.value]
                        ? chartText({
                          METRIC: 'sizeChartPanel.unitMetric',
                          IMPERIAL: 'sizeChartPanel.unitImperial',
                        }[opt.value], opt.label)
                        : opt.label)}</option>
                    ))}
                  </select>
                </label>
              </div>
            </div>
            <div className="tko-batch-modal__foot">
              <button
                type="button"
                className="tko-btn tko-btn--ghost tko-btn--sm"
                disabled={saving}
                onClick={() => setSaveMetaOpen(false)}
              >
                {chartText('sizeCreate.backEdit', fixedUi('返回修改'))}
              </button>
              <button
                type="button"
                className="tko-btn tko-btn--primary tko-btn--sm"
                disabled={saving}
                onClick={() => void submit()}
              >
                {saving ? chartText('tiktokPublish.saving', fixedUi("保存中…")) : chartText('sizeCreate.confirmSave', fixedUi('确认保存'))}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>,
    document.body,
  );
}
